import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import { TextInput } from "../../../../form/textInput";
import { updateNodeValue } from "../../../../../services/method";
import { webURL } from "../../../../../services/API";
import { operationType, selectDataType } from "../../../../../services/types";
import { AuthenticateSelect } from "../../../../auth/select";
import { Select } from "../../../../form/select";
import { Main, Styled } from "./style";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";

type ERPNextProps = NodeProps<operationType> & {
    setChildMinimize?: (fn: (id: string) => void) => void;
    setChildSettings?: (fn: (id: string) => void) => void;
};

// Resource (DocType) options
const resourceOptions: selectDataType[] = [
    { id: 1, name: 'Lead', refId: 'Lead', isActive: true },
    { id: 2, name: 'Customer', refId: 'Customer' },
    { id: 3, name: 'Contact', refId: 'Contact' },
];

// ==================== LEAD OPTIONS ====================
const leadStatusOptions: selectDataType[] = [
    { id: 1, name: 'Lead', refId: 'Lead', isActive: true },
    { id: 2, name: 'Open', refId: 'Open' },
    { id: 3, name: 'Replied', refId: 'Replied' },
    { id: 4, name: 'Opportunity', refId: 'Opportunity' },
    { id: 5, name: 'Quotation', refId: 'Quotation' },
    { id: 6, name: 'Interested', refId: 'Interested' },
    { id: 7, name: 'Converted', refId: 'Converted' },
    { id: 8, name: 'Do Not Contact', refId: 'Do Not Contact' },
];

const leadSourceOptions: selectDataType[] = [
    { id: 1, name: 'Website', refId: 'Website', isActive: true },
    { id: 2, name: 'Referral', refId: 'Referral' },
    { id: 3, name: 'Campaign', refId: 'Campaign' },
    { id: 4, name: 'Cold Calling', refId: 'Cold Calling' },
    { id: 5, name: 'Advertisement', refId: 'Advertisement' },
    { id: 6, name: 'Social Media', refId: 'Social Media' },
    { id: 7, name: 'Existing Customer', refId: 'Existing Customer' },
];

// ==================== CUSTOMER OPTIONS ====================
const customerTypeOptions: selectDataType[] = [
    { id: 1, name: 'Individual', refId: 'Individual', isActive: true },
    { id: 2, name: 'Company', refId: 'Company' },
];

const customerGroupOptions: selectDataType[] = [
    { id: 1, name: 'All Customer Groups', refId: 'All Customer Groups', isActive: true },
    { id: 2, name: 'Commercial', refId: 'Commercial' },
    { id: 3, name: 'Government', refId: 'Government' },
    { id: 4, name: 'Non Profit', refId: 'Non Profit' },
];

// ==================== OPPORTUNITY OPTIONS ====================
const opportunityFromOptions: selectDataType[] = [
    { id: 1, name: 'Lead', refId: 'Lead', isActive: true },
    { id: 2, name: 'Customer', refId: 'Customer' },
];

const opportunityStatusOptions: selectDataType[] = [
    { id: 1, name: 'Open', refId: 'Open', isActive: true },
    { id: 2, name: 'Quotation', refId: 'Quotation' },
    { id: 3, name: 'Converted', refId: 'Converted' },
    { id: 4, name: 'Lost', refId: 'Lost' },
    { id: 5, name: 'Replied', refId: 'Replied' },
    { id: 6, name: 'Closed', refId: 'Closed' },
];

const salesStageOptions: selectDataType[] = [
    { id: 1, name: 'Prospecting', refId: 'Prospecting', isActive: true },
    { id: 2, name: 'Qualification', refId: 'Qualification' },
    { id: 3, name: 'Needs Analysis', refId: 'Needs Analysis' },
    { id: 4, name: 'Value Proposition', refId: 'Value Proposition' },
    { id: 5, name: 'Proposal/Quote', refId: 'Proposal/Quote' },
    { id: 6, name: 'Negotiation', refId: 'Negotiation' },
    { id: 7, name: 'Closed Won', refId: 'Closed Won' },
    { id: 8, name: 'Closed Lost', refId: 'Closed Lost' },
];

// ==================== CONTACT OPTIONS ====================
const salutationOptions: selectDataType[] = [
    { id: 1, name: 'Mr', refId: 'Mr', isActive: true },
    { id: 2, name: 'Mrs', refId: 'Mrs' },
    { id: 3, name: 'Ms', refId: 'Ms' },
    { id: 4, name: 'Dr', refId: 'Dr' },
    { id: 5, name: 'Prof', refId: 'Prof' },
];

// ==================== QUOTATION/ORDER STATUS ====================
const quotationStatusOptions: selectDataType[] = [
    { id: 1, name: 'Draft', refId: 'Draft', isActive: true },
    { id: 2, name: 'Open', refId: 'Open' },
    { id: 3, name: 'Ordered', refId: 'Ordered' },
    { id: 4, name: 'Lost', refId: 'Lost' },
    { id: 5, name: 'Cancelled', refId: 'Cancelled' },
];

const orderStatusOptions: selectDataType[] = [
    { id: 1, name: 'Draft', refId: 'Draft', isActive: true },
    { id: 2, name: 'To Deliver and Bill', refId: 'To Deliver and Bill' },
    { id: 3, name: 'To Bill', refId: 'To Bill' },
    { id: 4, name: 'To Deliver', refId: 'To Deliver' },
    { id: 5, name: 'Completed', refId: 'Completed' },
    { id: 6, name: 'Cancelled', refId: 'Cancelled' },
];

// Operation options generator based on DocType
const getOperationOptions = (doctype: string): selectDataType[] => {
    const doctypeName = doctype === 'custom' ? 'Record' : doctype;
    return [
        { id: 0, name: 'Let AI Agent Decide', refId: 'agent_decide' },
        { id: 1, name: `Create a new ${doctypeName}`, refId: 'create', isActive: true },
        { id: 2, name: `Get all ${doctypeName}s`, refId: 'read_list' },
        { id: 3, name: `Get ${doctypeName} Details`, refId: 'read_single' },
        { id: 4, name: `Update ${doctypeName}`, refId: 'update' },
        { id: 5, name: `Delete ${doctypeName}`, refId: 'delete' },
    ];
};

export function ERPNext({ id, data, setChildMinimize, setChildSettings }: ERPNextProps) {
    const [resources, setResources] = React.useState<selectDataType[]>(resourceOptions);
    const [operations, setOperations] = React.useState<selectDataType[]>(getOperationOptions('Lead'));
    // Lead dropdowns
    const [leadStatuses, setLeadStatuses] = React.useState<selectDataType[]>(leadStatusOptions);
    const [leadSources, setLeadSources] = React.useState<selectDataType[]>(leadSourceOptions);
    // Customer dropdowns
    const [customerTypes, setCustomerTypes] = React.useState<selectDataType[]>(customerTypeOptions);
    const [customerGroups, setCustomerGroups] = React.useState<selectDataType[]>(customerGroupOptions);
    // Opportunity dropdowns
    const [opportunityFroms, setOpportunityFroms] = React.useState<selectDataType[]>(opportunityFromOptions);
    const [opportunityStatuses, setOpportunityStatuses] = React.useState<selectDataType[]>(opportunityStatusOptions);
    const [salesStages, setSalesStages] = React.useState<selectDataType[]>(salesStageOptions);
    // Contact dropdowns
    const [salutations, setSalutations] = React.useState<selectDataType[]>(salutationOptions);
    // Quotation & Order dropdowns
    const [quotationStatuses, setQuotationStatuses] = React.useState<selectDataType[]>(quotationStatusOptions);
    const [orderStatuses, setOrderStatuses] = React.useState<selectDataType[]>(orderStatusOptions);

    const { setNodes } = useReactFlow();
    const conn = data.value?.connection;
    const getERPNextCredentials = () => conn ? { baseUrl: conn.baseUrl, apiKey: conn.apiKey, apiSecret: conn.apiSecret } : null;
    const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
    const [isExpanded, setIsExpanded] = React.useState(false);

    // --
    React.useEffect(() => {
        if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
        if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setChildMinimize, setChildSettings]);

    // Initialize resource and operation
    React.useEffect(() => {
        if (!data.value) {
            updateNodeValue(id, setNodes, { doctype: 'Lead', operation: 'create' });
        } else {
            const currentDoctype = data.value.doctype || 'Lead';
            const currentOperation = data.value.operation || 'create';

            // Find the resource name for operations
            const resourceName = resourceOptions.find(r => r.refId === currentDoctype)?.name ||
                (currentDoctype === 'custom' ? 'Record' : currentDoctype);

            setResources(resources => resources.map(r => ({ ...r, isActive: r.refId === currentDoctype || (r.refId === 'custom' && !resourceOptions.find(o => o.refId === currentDoctype)) })));

            // Set operations with DocType-specific names
            const newOps = getOperationOptions(resourceName);
            setOperations(newOps.map(o => ({ ...o, isActive: o.refId === currentOperation })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle connection authentication
    const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
        if (type === 'add') {
            updateNodeValue(id, setNodes, { connection: connectionData });
            // Test connection and fetch initial records if needed
            try {
                await axios.post(`${webURL}/crm/erpnext/test`, { baseUrl: connectionData.baseUrl, apiKey: connectionData.apiKey, apiSecret: connectionData.apiSecret });
                // Fetch initial records based on current doctype
                await fetchRecords(connectionData);
            } catch (err: any) {
                console.error('Failed to connect to ERPNext:', err.response?.data || err.message || err);
            }
        } else if (type === 'remove') {
            updateNodeValue(id, setNodes, { connection: null, records: [] });
        }
    };

    // Fetch records for the current doctype
    const fetchRecords = async (connData?: any) => {
        const c = connData || conn;
        if (!c) return;
        const doctype = data.value?.doctype || 'Lead';
        if (doctype === 'custom' && !data.value?.customDoctype) return;

        const actualDoctype = doctype === 'custom' ? data.value?.customDoctype : doctype;

        try {
            const records = (await axios.post(`${webURL}/crm/erpnext/${actualDoctype}`, { baseUrl: c.baseUrl, apiKey: c.apiKey, apiSecret: c.apiSecret })).data;
            updateNodeValue(id, setNodes, {
                records: records.map((item: any, index: number) => ({
                    name: item.name,
                    label: item.lead_name || item.customer_name || item.first_name || item.address_title || item.party_name || item.name,
                    isActive: index === 0
                }))
            });
        } catch (err) {
            console.error('Failed to fetch records:', err);
            updateNodeValue(id, setNodes, { records: [] });
        }
    };

    // Handle input changes
    const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string, isInfo?: boolean) => {
        if (keyRef) {
            if (isInfo) setNodes(nodes => nodes.map(node => {
                if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, info: { ...node.data.value.info, [keyRef]: e.target.value } } } }
                else return node;
            }));
            else updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
        }
    };

    // Handle resource (doctype) selection
    const handleSelectResource = async (selectId: number) => {
        const refId = resources.find(r => r.id === selectId)?.refId || 'Lead';
        const resourceName = resources.find(r => r.id === selectId)?.name || 'Lead';
        setResources(resources => resources.map(r => ({ ...r, isActive: r.id === selectId })));

        // Update operations with new DocType-specific names
        const currentOp = operations.find(o => o.isActive)?.refId || 'create';
        const newOps = getOperationOptions(resourceName);
        setOperations(newOps.map(o => ({ ...o, isActive: o.refId === currentOp })));

        updateNodeValue(id, setNodes, { doctype: refId, records: [], info: {} });

        // Fetch records for the new doctype
        if (refId !== 'custom' && conn) {
            setTimeout(() => fetchRecords(), 100);
        }
    };

    // Handle operation selection
    const handleSelectOperation = (selectId: number) => {
        const refId = operations.find(o => o.id === selectId)?.refId || 'create';
        setOperations(operations => operations.map(o => ({ ...o, isActive: o.id === selectId })));
        updateNodeValue(id, setNodes, { operation: refId });

        // Refresh records for read/update/delete operations
        if (['read_single', 'update', 'delete'].includes(refId) && conn) {
            fetchRecords();
        }
    };

    // Handle record selection - fetch details and populate form
    const handleSelectRecord = async (selectId: number) => {
        const records = data.value?.records?.map((item: any, index: number) => ({ ...item, isActive: index === selectId - 1 })) || [];
        const selectedRecord = records.find((r: any) => r.isActive);

        // Fetch record details and populate form fields
        if (selectedRecord && conn) {
            const doctype = data.value?.doctype || 'Lead';
            try {
                const details = (await axios.post(`${webURL}/crm/erpnext/${doctype}/${encodeURIComponent(selectedRecord.name)}`, { baseUrl: conn.baseUrl, apiKey: conn.apiKey, apiSecret: conn.apiSecret })).data;
                // Populate info with fetched details
                updateNodeValue(id, setNodes, {
                    records,
                    info: {
                        lead_name: details.lead_name,
                        customer_name: details.customer_name,
                        first_name: details.first_name,
                        last_name: details.last_name,
                        status: details.status,
                        source: details.source,
                        email_id: details.email_id,
                        mobile_no: details.mobile_no,
                        phone: details.phone,
                        company_name: details.company_name,
                        territory: details.territory,
                        customer_type: details.customer_type,
                        customer_group: details.customer_group,
                        website: details.website,
                        salutation: details.salutation,
                        designation: details.designation,
                    }
                });
            } catch (err) {
                console.error('Failed to fetch record details:', err);
                updateNodeValue(id, setNodes, { records });
            }
        } else {
            updateNodeValue(id, setNodes, { records });
        }
    };

    // Handle lead status selection
    const handleSelectStatus = (selectId: number) => {
        const refId = leadStatuses.find(s => s.id === selectId)?.refId || 'Open';
        setLeadStatuses(statuses => statuses.map(s => ({ ...s, isActive: s.id === selectId })));
        setNodes(nodes => nodes.map(node => {
            if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, info: { ...node.data.value?.info, status: refId } } } };
            else return node;
        }));
    };

    // Handle lead source selection
    const handleSelectSource = (selectId: number) => {
        const refId = leadSources.find(s => s.id === selectId)?.refId || 'Website';
        setLeadSources(sources => sources.map(s => ({ ...s, isActive: s.id === selectId })));
        setNodes(nodes => nodes.map(node => {
            if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, info: { ...node.data.value?.info, source: refId } } } };
            else return node;
        }));
    };

    // Generic handler for dropdown selections
    const handleDropdownSelect = (field: string, options: selectDataType[], setOptions: React.Dispatch<React.SetStateAction<selectDataType[]>>, selectId: number, defaultValue: string) => {
        const refId = options.find(s => s.id === selectId)?.refId || defaultValue;
        setOptions(opts => opts.map(s => ({ ...s, isActive: s.id === selectId })));
        setNodes(nodes => nodes.map(node => {
            if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, info: { ...node.data.value?.info, [field]: refId } } } };
            else return node;
        }));
    };

    // Computed values
    const currentDoctype = data.value?.doctype || 'Lead';
    const currentOperation = data.value?.operation || 'create';
    const isAgentDecide = currentOperation === 'agent_decide';
    const isWarning = !data.value?.connection;
    const showRecordSelector = !isAgentDecide && ['read_single', 'update', 'delete'].includes(currentOperation);
    const showFormFields = !isAgentDecide && ['create', 'update'].includes(currentOperation);
    const showCustomDoctype = !isAgentDecide && currentDoctype === 'custom';

    // Get form fields based on doctype
    const renderFormFields = () => {
        const doctype = currentDoctype === 'custom' ? 'custom' : currentDoctype;

        switch (doctype) {
            case 'Lead':
                return (
                    <div className="scroll nowheel">
                        <TextInput keyRef="lead_name" title="Name " placeholder="Name (required)" value={data.value?.info?.lead_name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <Select title="Status" data={leadStatuses} handleSelectItem={(selectId) => handleSelectStatus(selectId)} isSmall={true} />
                        <TextInput keyRef="email_id" title="Email" placeholder="email@example.com" value={data.value?.info?.email_id} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="mobile_no" title="Mobile" placeholder="+1234567890" value={data.value?.info?.mobile_no} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="company_name" title="Company" placeholder="Company name" value={data.value?.info?.company_name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <Select title="Source" data={leadSources} handleSelectItem={(selectId) => handleSelectSource(selectId)} isSmall={true} />
                        <TextInput keyRef="territory" title="Territory" placeholder="Territory" value={data.value?.info?.territory} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                    </div>
                );
            case 'Customer':
                return (
                    <div className="scroll nowheel">
                        <TextInput keyRef="customer_name" title="Name " placeholder="Customer name (required)" value={data.value?.info?.customer_name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <Select title="Type" data={customerTypes} handleSelectItem={(selectId) => handleDropdownSelect('customer_type', customerTypes, setCustomerTypes, selectId, 'Individual')} isSmall={true} />
                        <Select title="Group" data={customerGroups} handleSelectItem={(selectId) => handleDropdownSelect('customer_group', customerGroups, setCustomerGroups, selectId, 'All Customer Groups')} isSmall={true} />
                        <TextInput keyRef="territory" title="Territory" placeholder="Territory" value={data.value?.info?.territory} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="email_id" title="Email" placeholder="email@example.com" value={data.value?.info?.email_id} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="mobile_no" title="Phone" placeholder="+1234567890" value={data.value?.info?.mobile_no} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="website" title="Website" placeholder="www.example.com" value={data.value?.info?.website} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                    </div>
                );
            case 'Opportunity':
                return (
                    <div className="scroll nowheel">
                        <Select title="From *" data={opportunityFroms} handleSelectItem={(selectId) => handleDropdownSelect('opportunity_from', opportunityFroms, setOpportunityFroms, selectId, 'Lead')} isSmall={true} />
                        <TextInput keyRef="party_name" title="Party Name *" placeholder="Lead/Customer name" value={data.value?.info?.party_name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <Select title="Status" data={opportunityStatuses} handleSelectItem={(selectId) => handleDropdownSelect('status', opportunityStatuses, setOpportunityStatuses, selectId, 'Open')} isSmall={true} />
                        <Select title="Sales Stage" data={salesStages} handleSelectItem={(selectId) => handleDropdownSelect('sales_stage', salesStages, setSalesStages, selectId, 'Prospecting')} isSmall={true} />
                        <TextInput keyRef="expected_closing" title="Expected Closing" placeholder="2024-12-31" value={data.value?.info?.expected_closing} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="opportunity_amount" title="Amount" placeholder="10000" value={data.value?.info?.opportunity_amount} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="probability" title="Probability (%)" placeholder="50" value={data.value?.info?.probability} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                    </div>
                );
            case 'Contact':
                return (
                    <div className="scroll nowheel">
                        <Select title="Salutation" data={salutations} handleSelectItem={(selectId) => handleDropdownSelect('salutation', salutations, setSalutations, selectId, 'Mr')} isSmall={true} />
                        <TextInput keyRef="first_name" title="First Name" placeholder="First name" value={data.value?.info?.first_name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="last_name" title="Last Name" placeholder="Last name" value={data.value?.info?.last_name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="email_id" title="Email" placeholder="email@example.com" value={data.value?.info?.email_id} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="phone" title="Phone" placeholder="+1234567890" value={data.value?.info?.phone} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="mobile_no" title="Mobile" placeholder="+1234567890" value={data.value?.info?.mobile_no} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="company_name" title="Company" placeholder="Company name" value={data.value?.info?.company_name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                    </div>
                );
            case 'Quotation':
                return (
                    <div className="scroll nowheel">
                        <TextInput keyRef="party_name" title="Customer *" placeholder="Customer name" value={data.value?.info?.party_name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <Select title="Status" data={quotationStatuses} handleSelectItem={(selectId) => handleDropdownSelect('status', quotationStatuses, setQuotationStatuses, selectId, 'Draft')} isSmall={true} />
                        <TextInput keyRef="transaction_date" title="Date" placeholder="2024-12-31" value={data.value?.info?.transaction_date} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="valid_till" title="Valid Till" placeholder="2024-01-31" value={data.value?.info?.valid_till} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="grand_total" title="Grand Total" placeholder="10000" value={data.value?.info?.grand_total} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="currency" title="Currency" placeholder="USD" value={data.value?.info?.currency} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                    </div>
                );
            case 'Sales Order':
                return (
                    <div className="scroll nowheel">
                        <TextInput keyRef="customer" title="Customer *" placeholder="Customer name" value={data.value?.info?.customer} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <Select title="Status" data={orderStatuses} handleSelectItem={(selectId) => handleDropdownSelect('status', orderStatuses, setOrderStatuses, selectId, 'Draft')} isSmall={true} />
                        <TextInput keyRef="transaction_date" title="Date" placeholder="2024-12-31" value={data.value?.info?.transaction_date} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="delivery_date" title="Delivery Date" placeholder="2024-01-15" value={data.value?.info?.delivery_date} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="grand_total" title="Grand Total" placeholder="10000" value={data.value?.info?.grand_total} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                        <TextInput keyRef="currency" title="Currency" placeholder="USD" value={data.value?.info?.currency} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                    </div>
                );
            default:
                return null;
        }
    };

    // Prepare records for Select component
    const recordsForSelect = data.value?.records?.map((item: any, index: number) => ({
        id: index + 1,
        name: item.label || item.name,
        isActive: item.isActive
    })) || [];

    return (<>
        <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
        <Main>
            {isWarning ? <>
                <div id="warning"></div>
                <span id="warningIcon"><IoWarningOutline size={21} /></span>
            </> : data.output ? <>
                <div id="ran"></div>
                <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
            </> : null}
            {isExpanded ? <ExpandedBlock
                data={{ id, ...data }}
                handleClose={() => setIsExpanded(false)}
                isWarning={isWarning}
                style={{ width: '400px' }}
            ><Styled>
                    <AuthenticateSelect type="erpnext" defaultId={data.value?.connection?.id} handleAuthenticate={handleAuthenticate} background={data.background} withLine />

                    {/* Resource (DocType) Select */}
                    {!isAgentDecide && (
                        <Select title="DocType" data={resources} handleSelectItem={(selectId) => handleSelectResource(selectId)} isSmall={true} />
                    )}

                    {/* Custom DocType Input */}
                    {showCustomDoctype && (
                        <TextInput
                            keyRef="customDoctype"
                            title="Custom DocType"
                            placeholder="Enter DocType name"
                            value={data.value?.customDoctype}
                            handleChange={(e, keyRef) => {
                                handleChangeInput(e, keyRef);
                                setTimeout(() => fetchRecords(), 500);
                            }}
                        />
                    )}

                    {/* Operation Select */}
                    <Select title="Operation" data={operations} handleSelectItem={(selectId) => handleSelectOperation(selectId)} isSmall={true} />

                    {/* Record Selector (for read_single, update, delete) */}
                    {showRecordSelector && (
                        <Select
                            title="Record"
                            data={recordsForSelect}
                            handleSelectItem={(selectId) => handleSelectRecord(selectId)}
                            isSmall={true}
                        />
                    )}

                    {/* Form Fields (for create, update) */}
                    {showFormFields && renderFormFields()}

                </Styled></ExpandedBlock> : null}
        </Main>
    </>);
}